import {
  type Attribute,
  type CommandProps,
  mergeAttributes,
} from '@tiptap/core';
import BaseTableHeader from '@tiptap/extension-table-header';
import { isInTable, selectedRect } from './proseMirrorTypes';
import { Table as BaseTable, type TableOptions } from '@tiptap/extension-table';
import BaseTableCell from '@tiptap/extension-table-cell';
import type { EditorState } from '@tiptap/pm/state';
import { ReactNodeViewRenderer } from '@tiptap/react';
import {
  defaultTableNodeViewLabels,
  TableCellNodeView,
  TableHeaderNodeView,
  TableNodeViewLabels,
} from './TableNodeView';
import type { DOMOutputSpec } from '@tiptap/pm/model';

declare module '@tiptap/core' {
  interface Storage {
    table: {
      labels: TableNodeViewLabels;
      menuButtonLabel: string;
    };
  }
}

declare module '@tiptap/react' {
  interface Commands<ReturnType> {
    ardoqTable: {
      setColumnAlignment: (align?: 'left' | 'center' | 'right') => ReturnType;
      updateAlignmentInRowBefore: () => ReturnType;
      updateAlignmentInRowAfter: () => ReturnType;
    };
  }
}

const AlignAttribute: Attribute = {
  default: 'left',
  parseHTML: element => element.getAttribute('align'),
  renderHTML: attributes => {
    return {
      align: attributes.align,
    };
  },
};

const updateAlignmentInNewRow = (
  state: EditorState,
  where: 'after' | 'before'
) => {
  const rect = selectedRect(state);

  // Current row in focus we want to copy the alignment from
  const currentRow = rect.map.cellsInRect({
    left: 0,
    top: rect.top,
    right: rect.map.width,
    bottom: rect.bottom,
  });

  // Newly inserted row
  const newRow = rect.map.cellsInRect({
    left: 0,
    top: where === 'before' ? rect.top - 1 : rect.top + 1,
    right: rect.map.width,
    bottom: where === 'before' ? rect.bottom - 1 : rect.bottom + 1,
  });

  newRow.forEach((cellRelativePos: number, i: number) => {
    const cellBeforeOrAfter = rect.table.nodeAt(currentRow[i]);
    if (cellBeforeOrAfter) {
      const newCellPosition = cellRelativePos + rect.tableStart;
      state.tr.setNodeMarkup(newCellPosition, null, {
        align: cellBeforeOrAfter.attrs.align,
      });
    }
  });
};

type ArdoqTableOptions = TableOptions & {
  labels: TableNodeViewLabels;
  menuButtonLabel: string;
};

export const Table = BaseTable.extend<ArdoqTableOptions>({
  addOptions() {
    return {
      ...(this.parent?.() ?? ({} as TableOptions)),
      labels: defaultTableNodeViewLabels,
      menuButtonLabel: 'Menu',
    };
  },

  addStorage() {
    return {
      ...(this.parent?.() ?? {}),
      labels: defaultTableNodeViewLabels,
      menuButtonLabel: 'Menu',
    };
  },

  onCreate(event) {
    this.parent?.(event);
    this.storage.labels = this.options.labels;
    this.storage.menuButtonLabel = this.options.menuButtonLabel;
  },

  // Override the default renderHTML to remove the colgroup for now.
  renderHTML({ HTMLAttributes }: { HTMLAttributes: Record<string, string> }) {
    const table: DOMOutputSpec = [
      'table',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
      ['tbody', 0],
    ];
    return table;
  },

  addCommands() {
    return {
      ...this.parent?.(),
      setColumnAlignment:
        (newAlignment = 'left') =>
        ({ state, dispatch }: CommandProps) => {
          if (!isInTable(state)) return false;
          const rect = selectedRect(state);
          const colCells = rect.map.cellsInRect({
            left: rect.left,
            top: 0,
            right: rect.right,
            bottom: rect.map.height,
          });
          colCells.forEach(cellRelativePos => {
            const cell = rect.table.nodeAt(cellRelativePos);
            if (!cell) return;
            state.tr.setNodeMarkup(cellRelativePos + rect.tableStart, null, {
              ...cell.attrs,
              align: newAlignment,
            });
          });

          if (dispatch) {
            dispatch(state.tr);
          }

          return true;
        },
      updateAlignmentInRowBefore:
        () =>
        ({ state }: CommandProps) => {
          if (!isInTable(state)) return false;
          updateAlignmentInNewRow(state, 'before');
          return true;
        },
      updateAlignmentInRowAfter:
        () =>
        ({ state }: CommandProps) => {
          if (!isInTable(state)) return false;
          updateAlignmentInNewRow(state, 'after');
          return true;
        },
      /**
       * The current way we do alignment needs to be reworked
       * to enable colspan and rowspan support.
       */
      mergeCells: () => () => {
        throw new Error('Not supported');
      },
      splitCell: () => () => {
        throw new Error('Not supported');
      },
      mergeOrSplit: () => () => {
        throw new Error('Not supported');
      },
    };
  },
});

export const TableHeader = BaseTableHeader.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      align: AlignAttribute,
    };
  },
  addNodeView() {
    return ReactNodeViewRenderer(TableHeaderNodeView, {
      as: 'th',
      attrs: ({ node }) => ({
        align: node.attrs.align,
        colspan: node.attrs.colspan,
        rowspan: node.attrs.rowspan,
      }),
    });
  },
});

export const TableCell = BaseTableCell.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      align: AlignAttribute,
    };
  },
  addNodeView() {
    return ReactNodeViewRenderer(TableCellNodeView, {
      as: 'td',
      attrs: ({ node }) => ({
        align: node.attrs.align,
        colspan: node.attrs.colspan,
        rowspan: node.attrs.rowspan,
      }),
    });
  },
});
